import React from 'react'
import { Provider } from 'react-redux'
import { BrowserRouter } from 'react-router-dom'

import { QueryClient, QueryClientProvider } from '@tanstack/react-query'
import { ReactQueryDevtools } from '@tanstack/react-query-devtools'
import { ConnectedRouter } from 'connected-react-router'

import { ThemeProvider } from 'antd-style'

import httpClient from '@emmacare/common/api/httpClient'
import { DesignSystemDialogsRegistrar } from '@emmacare/common/design-system/components/Dialog'
import { DesignSystemMessageRegistrar } from '@emmacare/common/design-system/components/Message'
import { DesignSystemNotificationRegistrar } from '@emmacare/common/design-system/components/Notification'
import { ANT_THEME_PREFIX } from '@emmacare/common/theme/config'
import type { ICustomToken } from '@emmacare/common/theme/customToken.types'
import GlobalStyle from '@emmacare/common/theme/default/GlobalStyle'
import customToken from '@emmacare/common/theme/default/customToken.json'
import defaultTheme from '@emmacare/common/theme/default/default.theme.json'

import { setNoInternetConnection } from 'src/store/modules/actionCreators'
import { logout, refreshAuthTokens } from 'src/store/modules/user'

import { API_BASE_URL, DEFAULT_HEADERS, DEVTOOLS } from 'src/constants'
import { history, store } from 'src/store'

import DatadogUserTracker from './components/DatadogUserTracker'
import Root from './components/Root'

httpClient.initialize({
  store,
  onLogout: logout,
  onRefreshToken: refreshAuthTokens,
  onNoInternetConnection: setNoInternetConnection,
  API_URL: API_BASE_URL,
  API_HEADERS: DEFAULT_HEADERS
})

declare module 'antd-style' {
  // eslint-disable-next-line @typescript-eslint/no-empty-interface
  export interface CustomToken extends ICustomToken {}
}

export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
      retry: 0,
      gcTime: 0,
      staleTime: 0,
      retryOnMount: false
      // suspense: true
    }
  }
})

type AppProps = {
  maintenance?: boolean
}

const App = ({ maintenance = false }: AppProps) => {
  // TODO eject pdf to app-pdf with own theme
  if (location.pathname.startsWith('/pdf/')) {
    defaultTheme.token.fontFamily = "'OpenSans', sans-serif"
  }
  return (
    <ThemeProvider<ICustomToken>
      customToken={customToken}
      theme={defaultTheme}
      prefixCls={ANT_THEME_PREFIX}
    >
      <GlobalStyle />
      <QueryClientProvider client={queryClient}>
        <Provider store={store}>
          <BrowserRouter>
            <ConnectedRouter history={history}>
              <DatadogUserTracker />
              {/* <React.StrictMode> */}
              <Root maintenance={maintenance} />
              {/* </React.StrictMode> */}
            </ConnectedRouter>
          </BrowserRouter>
        </Provider>
        {DEVTOOLS && (
          <ReactQueryDevtools
            initialIsOpen={false}
            buttonPosition="bottom-left"
          />
        )}
      </QueryClientProvider>
      <DesignSystemDialogsRegistrar />
      <DesignSystemMessageRegistrar />
      <DesignSystemNotificationRegistrar />
    </ThemeProvider>
  )
}

export default App
